import EmailOutlined from '@mui/icons-material/EmailOutlined'
import EventIcon from '@mui/icons-material/Event'
import PhoneOutlined from '@mui/icons-material/PhoneOutlined'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar } from '~/components/Atoms/Avatar'
import { Button } from '~/components/Atoms/Button'

import { useContacts } from '~/lib/company'
import { Contact } from '~/types/api'

export function ContactCard() {
  const theme = useTheme()
  const { t } = useTranslation()
  const { data } = useContacts()

  const [displayPhone, setDisplayPhone] = useState(false)

  return (
    <Box
      sx={{
        width: '400px',
        [theme.breakpoints.down('xl')]: {
          width: '300px',
        },
      }}
      padding={theme.spacing.XXL}
      borderRadius={theme.radius.L}
      bgcolor={theme.palette.common.white}
      display="flex"
      flexShrink={0}
      height="fit-content"
      flexDirection="column"
    >
      <Box display="flex" flexDirection="column" gap={theme.spacing.XL}>
        <Box display="flex" flexDirection="column" gap={theme.spacing.XXL} justifyContent="center" alignItems="center">
          <Avatar state="image" src={data[0].photo?.url} variant="largeDisplay" />
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
            <Typography variant="body-bold">{data[0]?.name}</Typography>
            <Typography variant="body" color={theme.palette.text.mainLight}>
              {t(`home:contact.title.${data[0].title}`)}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center" gap={theme.spacing['6XL']}>
          <Box padding="0">
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={theme.spacing.ML}
              onClick={() => {
                setDisplayPhone(!displayPhone)
              }}
              sx={{ cursor: 'pointer' }}
            >
              <Box
                display="flex"
                bgcolor={theme.palette.primary.lighter}
                borderRadius={theme.radius.full}
                alignItems="center"
                justifyContent="center"
                width={displayPhone ? 'unset' : theme.size[14]}
                padding={displayPhone ? `0 ${theme.spacing.L}` : 0}
                height={theme.size[14]}
              >
                {displayPhone ? (
                  <Typography variant="body" color={theme.palette.primary.active}>
                    {data[0].phone}
                  </Typography>
                ) : (
                  <PhoneOutlined
                    width={theme.size[10]}
                    height={theme.size[10]}
                    sx={{ color: theme.palette.primary.active }}
                  />
                )}
              </Box>
              <Typography variant="body" color={theme.palette.text.mainLight}>
                {t('home:contact.tel')}
              </Typography>
            </Box>
          </Box>
          <Box padding="0">
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={theme.spacing.ML}
              onClick={() => {
                location.href = 'mailTo:' + data[0]?.email
              }}
              sx={{ cursor: 'pointer' }}
            >
              <Box
                display="flex"
                bgcolor={theme.palette.primary.lighter}
                borderRadius={theme.radius.full}
                alignItems="center"
                justifyContent="center"
                width={theme.size[14]}
                height={theme.size[14]}
              >
                <EmailOutlined
                  width={theme.size[10]}
                  height={theme.size[10]}
                  sx={{ color: theme.palette.primary.active }}
                />
              </Box>
              <Typography variant="body" color={theme.palette.text.mainLight}>
                {t('home:contact.mail')}
              </Typography>
            </Box>
          </Box>
          <Box padding="0">
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={theme.spacing.ML}
              onClick={() => {
                location.href = data[0]?.calendar
              }}
              sx={{ cursor: 'pointer' }}
            >
              <Box
                display="flex"
                bgcolor={theme.palette.primary.lighter}
                borderRadius={theme.radius.full}
                alignItems="center"
                justifyContent="center"
                width={theme.size[14]}
                height={theme.size[14]}
              >
                <EventIcon
                  width={theme.size[10]}
                  height={theme.size[10]}
                  sx={{ color: theme.palette.primary.active }}
                />
              </Box>
              <Typography variant="body" color={theme.palette.text.mainLight}>
                {t('home:contact.rdv')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Stack
        padding={`${theme.spacing['5XL']} 0 ${theme.spacing['5XL']} 0`}
        display="flex"
        gap={theme.spacing['3XL']}
        justifyContent="space-between"
        divider={<Box display="flex" height={'1px'} width="100%" bgcolor={theme.palette.neutral.lightest} />}
      >
        {data.slice(1).map((contact) => (
          <ContactItem key={contact.name} contact={contact} />
        ))}
      </Stack>
    </Box>
  )
}

function ContactItem({ contact }: { contact: Contact }) {
  const theme = useTheme()
  const { t } = useTranslation()

  return (
    <Box display="flex" justifyContent="space-between" width="100%">
      <Box display="flex" gap={theme.spacing.XL} width="100%" alignItems="center">
        <Avatar flexShrink={0} variant="large" state="image" src={contact.photo?.url} />
        <Box display="flex" width="100%">
          <Box display="flex" flexDirection="column" width="100%" gap={theme.spacing.XS}>
            <Typography variant="body">{contact.name}</Typography>
            <Typography variant="body-small">{t(`home:contact.title.${contact.title}`)}</Typography>
          </Box>
        </Box>
        <Box display="flex">
          <Button
            onClick={() => {
              location.href = 'mailTo:' + contact.email
            }}
            startIcon={<EmailOutlined />}
          />
          {contact.calendar && (
            <Button
              onClick={() => {
                location.href = contact.calendar
              }}
              startIcon={<EventIcon />}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}
